import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/hooks/useLanguage';
import { useMunicipality, useLocalities } from '@/hooks/usePostalAreas';
import {
  getCountyBySlug,
  getMunicipalityBySlug,
  getLocalitiesByMunicipalityCode,
} from '@/lib/postal-snapshot';
import { PostalBreadcrumb } from '@/components/postal/PostalBreadcrumb';
import { PostalStatsCards } from '@/components/postal/PostalStatsCards';
import { PopulationChart } from '@/components/postal/PopulationChart';
import { IncomeChart } from '@/components/postal/IncomeChart';
import { PropertyChart } from '@/components/postal/PropertyChart';
import { PostalAreaGrid } from '@/components/postal/PostalAreaGrid';
import { SEO, getPlaceSchema, getBreadcrumbSchema } from '@/hooks/useSEO';

export default function PostalMunicipalityPage() {
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const { countySlug, municipalitySlug } = useParams<{
    countySlug: string;
    municipalitySlug: string;
  }>();

  // Snapshot-backed sync lookups so SSG renders with real names + headline
  // stats. Heavy chart data is fetched client-side via React Query.
  const countySnap = getCountyBySlug(countySlug);
  const muniSnap = getMunicipalityBySlug(countySnap?.code, municipalitySlug);
  const localitySnaps = muniSnap
    ? getLocalitiesByMunicipalityCode(muniSnap.code)
    : [];

  const { data: muniFresh } = useMunicipality(muniSnap?.code ?? '');
  const { data: localityFresh } = useLocalities(muniSnap?.code ?? '');

  const county = countySnap;
  const municipality = muniFresh ?? muniSnap;
  const localities = localityFresh ?? localitySnaps;

  if (!municipality) {
    return (
      <div className="container py-12">
        <p>{t('postal.notFound')}</p>
      </div>
    );
  }

  const countyPath = `/information/postnummer/${countySlug}`;
  const muniPath = `${countyPath}/${municipalitySlug}`;
  const muniUrl = `https://postnummerservice.se/${lang}${muniPath}`;
  const seoDescription = `${municipality.name} kommun i ${county?.name ?? ''} har ${municipality.population_total.toLocaleString('sv-SE')} invånare och ${municipality.postcode_count} postnummer. Se statistik om befolkning, inkomst och fastigheter.`;

  return (
    <div className="container py-6 md:py-12 lg:py-16">
      <SEO
        title={`${municipality.name} kommun – ${t('navigation.postalCodes')}`}
        description={seoDescription}
        path={muniPath}
        jsonLd={[
          getBreadcrumbSchema([
            { name: t('common.home'), url: `https://postnummerservice.se/${lang}` },
            { name: t('navigation.postalCodes'), url: `https://postnummerservice.se/${lang}/information/postnummer` },
            { name: county?.name ?? countySlug!, url: `https://postnummerservice.se/${lang}${countyPath}` },
            { name: municipality.name, url: muniUrl },
          ]),
          getPlaceSchema({
            name: municipality.name,
            description: seoDescription,
            url: muniUrl,
            containedIn: county?.name,
            population: municipality.population_total,
            area: municipality.area ?? undefined,
          }),
        ]}
      />
      <div className="max-w-5xl mx-auto space-y-5 md:space-y-8">
        <PostalBreadcrumb
          levels={[
            { label: county?.name ?? countySlug!, href: `/${lang}${countyPath}` },
            { label: municipality.name },
          ]}
        />

        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">{municipality.name}</h1>
          <p className="text-muted-foreground">{t('postal.municipalityDescription', { name: municipality.name, county: county?.name ?? '' })}</p>
          <p className="text-sm text-muted-foreground mt-2">{t('postal.municipalityIntro', { name: municipality.name, county: county?.name ?? '', population: municipality.population_total.toLocaleString('sv-SE'), postalCodes: municipality.postcode_count })}</p>
        </div>

        <PostalStatsCards
          population={municipality.population_total}
          postalCodes={municipality.postcode_count}
          area={municipality.area ?? undefined}
          medianIncome={muniFresh?.income_data?.income_median}
        />

        {muniFresh && (
          <>
            <div className="grid gap-4 md:gap-6 lg:grid-cols-2">
              <PopulationChart data={muniFresh.population_data} />
              <IncomeChart data={muniFresh.income_data} />
            </div>

            <PropertyChart data={muniFresh.properties_data} />
          </>
        )}

        {localities.length > 0 && (
          <PostalAreaGrid
            areas={localities}
            buildHref={(l) => `/${lang}${muniPath}/${l.slug ?? l.code}`}
            childLabel={t('postal.localities')}
          />
        )}
      </div>
    </div>
  );
}
